import { useState } from 'react';
import type { ChatDocumentStatus, ChatReviewItem, DocumentCard } from '../lib/api';

/**
 * CHAT-DOCS Task 12: the card a made document shows in the chat - a listing agreement, an agency disclosure, an LOI.
 *
 * ⭐⭐ "FILE IT" SENDS THE CHAT MESSAGE `file it: <title> v<N>`. It never files directly: filing always goes through
 *   the file_document tool and every confirmation it asks for (which deal, a new deal, the stale draft). A button that
 *   called an endpoint would be a second door with none of those checks. ⭐ It NAMES this card's document (final
 *   wave): the tool resolves "<title> v<N>" against the chat, so tapping v2's button can never file the agreement's
 *   neighbour or every draft in the chat.
 * ⛔ Only a live DRAFT offers it. A superseded version is history, a filed one is done, and a needs-fixing one would
 *   be refused by the tool anyway - offering the button there would invite a tap that can only fail.
 * ⚠️ The panel is used on his phone (375px): everything wraps; nothing here may widen the column.
 */

const BADGE: Record<ChatDocumentStatus, { word: string; cls: string }> = {
  draft: { word: 'Draft', cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  filed: { word: 'Filed', cls: 'bg-green-50 text-green-800 border-green-200' },
  superseded: { word: 'Superseded', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  needs_fixing: { word: 'Needs fixing', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const Item = ({ item, cls }: { item: ChatReviewItem; cls: string }) => (
  <li className={`rounded border px-2 py-1 text-[11px] leading-snug break-words ${cls}`}>{item.text}</li>
);

export default function ChatDocumentCard({ card, onFile, disabled = false }: {
  card: DocumentCard;
  /** Sends a chat message through the panel's own send - called with exactly `file it: <title> v<N>`. */
  onFile: (message: string) => void;
  /** True while an answer is still arriving: the panel would drop the message anyway. */
  disabled?: boolean;
}) {
  const [showOks, setShowOks] = useState(false);
  const items = card.review?.items ?? [];
  const problems = items.filter((i) => i.level === 'problem');
  const notes = items.filter((i) => i.level === 'note');
  const oks = items.filter((i) => i.level === 'ok');
  const badge = BADGE[card.status] ?? BADGE.draft;
  const superseded = card.status === 'superseded';

  return (
    <div className={`mt-1 w-full max-w-full rounded-[20px] border border-gray-200 bg-white p-3 text-left ${superseded ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <p className="min-w-0 flex-1 text-[12.5px] font-semibold leading-snug text-gray-900 break-words">{card.title}</p>
        <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${badge.cls}`}>
          {`${badge.word} v${card.version}`}
        </span>
      </div>

      {problems.length || notes.length ? (
        <ul className="mt-1.5 space-y-1">
          {/* ⭐ Problems FIRST and red: they are what stops it being filed. Notes after, amber, like the panel's notes. */}
          {problems.map((p, j) => <Item key={`p${j}`} item={p} cls="border-red-200 bg-red-50 text-red-800" />)}
          {notes.map((n, j) => <Item key={`n${j}`} item={n} cls="border-amber-200 bg-amber-50 text-amber-900" />)}
        </ul>
      ) : null}
      {card.status === 'needs_fixing' ? (
        <p className="mt-1 text-[11px] font-medium text-red-700">Fix these before it can be filed.</p>
      ) : null}

      {/* The passed checks can run to a dozen - folded away, but counted, so he knows they were done. */}
      {oks.length ? (
        showOks ? (
          <ul className="mt-1 space-y-1">
            {oks.map((o, j) => <Item key={`o${j}`} item={o} cls="border-gray-200 bg-gray-50 text-gray-700" />)}
          </ul>
        ) : (
          <button
            type="button"
            onClick={() => setShowOks(true)}
            className="mt-1 text-[11px] text-gray-500 underline-offset-[3px] hover:underline"
          >
            {`Show the ${oks.length} check${oks.length === 1 ? '' : 's'} that passed`}
          </button>
        )
      ) : null}

      <div className="mt-1.5 flex flex-wrap gap-2">
        {card.url ? (
          <a
            href={card.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded border border-gray-300 px-2 py-1 text-[11.5px] font-medium text-gray-800 hover:bg-gray-50"
          >
            Open
          </a>
        ) : null}
        {card.status === 'draft' ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onFile(`file it: ${card.title} v${card.version}`)}
            className="rounded bg-primary-500 px-2 py-1 text-[11.5px] font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
          >
            File it
          </button>
        ) : null}
      </div>
    </div>
  );
}
